import { GetPreviousDataReturn } from '@/dashboards/shared/optimisation/previousData';
import type { OptimizedPlan } from '@/dashboards/shared/optimisation/optimisationSession';
import { ContributionAndSpendByPeriod } from '@/dashboards/types';
import { PeriodPlan } from '@/optimizer/periodPlan.type';

export type StructureMediaSpendSeasonalityPeriod = 'monthly' | 'weekly';

export type StructureMediaSpendSeasonalityReturn = ReturnType<
    typeof structureMediaSpendSeasonality
>;

export const structureMediaSpendSeasonality = (
    period: StructureMediaSpendSeasonalityPeriod,
    {
        variablesAndCategories,
        optimizedData,
        variableColorMap,
        categoryColorMap,
        variableNameMap,
        totalKpi,
    }: { optimizedData: OptimizedPlan | undefined } &
        Pick<GetPreviousDataReturn['roiCurves'], 'variablesAndCategories'> & {
            variableColorMap: Record<string, string>;
            categoryColorMap: Record<string, string>;
            variableNameMap: Record<string, string>;
            totalKpi: number;
        },
) => {
    const dataByCategory: ContributionAndSpendByPeriod = {};
    const dataByVariable: ContributionAndSpendByPeriod = {};

    const dates: string[] = [];
    let datesFilled = false;

    const planToIterateOn: PeriodPlan[] =
        period === 'monthly'
            ? optimizedData?.monthlyPlan || []
            : optimizedData?.weeklyPlan || [];

    const allVariables = Object.values(
        variablesAndCategories.byCategory,
    ).flat();

    const sumCoefficients = planToIterateOn.reduce(
        (sum, plan) => sum + (plan.coefficient || 0),
        0,
    );

    const totalContributionByDate: Record<string, number> = {};
    planToIterateOn.forEach((plan) => {
        const periodKey = plan.periodDate || `${period === 'monthly' ? 'Month' : 'Week'} ${plan.periodIndex}`;
        const periodKpi =
            sumCoefficients > 0
                ? ((plan.coefficient || 0) / sumCoefficients) * totalKpi
                : 0;
        totalContributionByDate[periodKey] = periodKpi;
    });

    Object.entries(variablesAndCategories.byCategory).forEach(
        ([categoryName, variables]) => {
            dataByCategory[categoryName] = {
                color: categoryColorMap[categoryName]!,
                data: {},
            };

            const categoryData: {
                [key: string]: { contribution: number; spend: number };
            } = {};

            planToIterateOn.forEach((plan) => {
                const periodKey = plan.periodDate || `${period === 'monthly' ? 'Month' : 'Week'} ${plan.periodIndex}`;

                if (!datesFilled) {
                    dates.push(periodKey as string);
                }

                variables.forEach((variable) => {
                    const variableDisplayName = variableNameMap[variable]!;

                    if (!dataByVariable[variableDisplayName]) {
                        dataByVariable[variableDisplayName] = {
                            color: variableColorMap[variable]!,
                            data: {},
                        };
                    }
                    if (!dataByVariable[variableDisplayName]!.data[periodKey]) {
                        dataByVariable[variableDisplayName]!.data[periodKey] = {
                            contribution: 0,
                            spend: 0,
                        };
                    }

                    if (!categoryData[periodKey]) {
                        categoryData[periodKey] = {
                            contribution: 0,
                            spend: 0,
                        };
                    }

                    const spendValue = plan[variable];
                    const spend = typeof spendValue === 'number' ? spendValue : 0;
                    const periodKpi =
                        sumCoefficients > 0
                            ? ((plan.coefficient || 0) / sumCoefficients) * totalKpi
                            : 0;
                    const periodTotalSpend = allVariables.reduce(
                        (s, v) => {
                            const val = plan[v];
                            return s + (typeof val === 'number' ? val : 0);
                        },
                        0,
                    );
                    const contribution =
                        periodTotalSpend > 0
                            ? (spend / periodTotalSpend) * periodKpi
                            : 0;

                    dataByVariable[variableDisplayName]!.data[
                        periodKey
                    ]!.spend += spend;
                    dataByVariable[variableDisplayName]!.data[
                        periodKey
                    ]!.contribution += contribution;

                    categoryData[periodKey]!.spend += spend;
                    categoryData[periodKey]!.contribution += contribution;
                });
            });

            datesFilled = true;
            dataByCategory[categoryName]!.data = categoryData;
        },
    );

    return {
        dates,
        dataByCategory,
        dataByVariable,
        totalContributionByDate,
    };
};
